import { merge, omit } from 'lodash';
import i18next from 'i18next';
import { type RouteObject } from 'react-router';

import { type SettingsState } from 'app/store/fuse/settingsSlice';
import { type NavigationItem } from 'app/store/fuse/navigationSlice';
import { type IsModuleDisabledType } from 'app/main/components/hooks/useIsModuleDisabled';

export type AppRouteAuthFunction = (params: { isModuleDisabled: IsModuleDisabledType; authorizer: any }) => boolean;

export type AppRouteGroup = {
	settings?: Partial<SettingsState>;
	auth?: AppRouteAuthFunction | null;
	routes: AppRoute[];
};

export type AppRoute = {
	settings?: Partial<SettingsState>;
	auth?: AppRouteAuthFunction | null;
} & RouteObject;

function getDerivedRolesFromAuthRole(authRole: string | string[] | null, appRoles?: string[] | null) {
	if (authRole === null) return [];
	if (!appRoles || appRoles === null) return null;

	let derivedRoles = Array<string>();

	if (Array.isArray(authRole)) {
		derivedRoles = Object.entries(appRoles)
			.filter(role => authRole.some(auth => role[1].includes(auth)))
			.map(role => role[0]);

		derivedRoles.push(...authRole);
	} else {
		derivedRoles = Object.entries(appRoles)
			.filter(role => role[1].includes(authRole))
			.map(role => role[0]);

		derivedRoles.push(authRole);
	}

	return derivedRoles;
}

function hasPermission(authArr?: string[] | null, userRole?: string | string[]) {
	if (authArr === null || authArr === undefined) {
		return userRole === null;
	}

	if (authArr.length === 0) {
		return !userRole || userRole.length === 0;
	}

	if (userRole && Array.isArray(userRole)) {
		return authArr.some(r => userRole.indexOf(r) >= 0);
	}

	return typeof userRole === 'string' ? authArr.includes(userRole) : false;
}

function getTranslatedRoutes(routes: NavigationItem[]) {
	// loop through every object in the array
	return routes.map(route => {
		if (route.translate && route.title) {
			route.title = i18next.t(`navigation:${route.translate}`);
		}

		if (route.children) {
			// run this function recursively on the children array
			route.children = getTranslatedRoutes(route.children);
		}

		return route;
	});
}

function getFlatNavigation(navigationItems: NavigationItem[], flatNavigation: NavigationItem[] = []) {
	for (let i = 0; i < navigationItems.length; i += 1) {
		const navItem = navigationItems[i];

		if (navItem.type === 'item') {
			flatNavigation.push({
				id: navItem.id,
				title: navItem.title,
				translate: navItem.translate,
				type: navItem.type,
				icon: navItem.icon,
				url: navItem.url,
				auth: navItem.auth || null
			});
		}

		if (navItem.type === 'collapse' || navItem.type === 'group') {
			if (navItem.children) {
				getFlatNavigation(navItem.children, flatNavigation);
			}
		}
	}
	return flatNavigation;
}

function updateNavItem(nav: NavigationItem[], id: string, item: NavigationItem) {
	return nav.map(_item => {
		if (_item.id === id) {
			return merge({}, _item, item);
		}

		if (_item.children) {
			return merge({}, _item, {
				children: updateNavItem(_item.children, id, item)
			});
		}

		return merge({}, _item);
	});
}

function removeNavItem(nav: NavigationItem[], id: string) {
	return nav
		.map(_item => {
			if (_item.id === id) {
				return null;
			}

			if (_item.children) {
				return merge({}, omit(_item, ['children']), {
					children: removeNavItem(_item.children, id)
				});
			}

			return merge({}, _item);
		})
		.filter(s => s);
}

function prependNavItem(nav: NavigationItem[], item: NavigationItem, parentId: string) {
	if (!parentId) {
		return [item, ...nav];
	}

	return nav.map(_item => {
		if (_item.id === parentId && _item.children) {
			return {
				_item,
				children: [item, ..._item.children]
			};
		}

		if (_item.children) {
			return merge({}, _item, {
				children: prependNavItem(_item.children, item, parentId)
			});
		}

		return merge({}, _item);
	});
}

function appendNavItem(nav: NavigationItem[], item: NavigationItem, parentId: string) {
	if (!parentId) {
		return [...nav, item];
	}

	return nav.map(_item => {
		if (_item.id === parentId && _item.children) {
			return {
				..._item,
				children: [..._item.children, item]
			};
		}

		if (_item.children) {
			return merge({}, _item, {
				children: appendNavItem(_item.children, item, parentId)
			});
		}

		return merge({}, _item);
	});
}

function filterDisabledModules(nav: NavigationItem[], modules: any[]) {
	const filteredNav: NavigationItem[] = [];

	nav.forEach(item => {
		const module = modules?.find(m => m.modu_title === item.id);
		if (!module?.modu_disabled) {
			if (item.children) {
				const children = filterDisabledModules(item.children, modules);
				if (children.length > 0) filteredNav.push({ ...item, children });
			} else filteredNav.push(item);
		}
	}, []);

	return filteredNav;
}

function filterRecursive(data: any[], predicate: any) {
	// if no data is sent in, return null, otherwise transform the data
	return !data
		? null
		: data.reduce<any[]>((list, entry) => {
				let clone: any = undefined;
				if (predicate(entry)) {
					// if the object matches the filter, clone it as it is
					clone = { ...entry };
				}
				if (entry.children != null) {
					// if the object has childrens, filter the list of children
					const children = filterRecursive(entry.children, predicate);
					if ((children?.length || 0) > 0) {
						// if any of the children matches, clone the parent object, overwrite
						// the children list with the filtered list
						clone = { ...entry, children };
					}
				}

				// if there's a cloned object, push it to the output list
				if (clone) {
					list.push(clone);
				}
				return list;
			}, []);
}

function setRoutes(config: AppRouteGroup, defaultAuth?: AppRouteAuthFunction) {
	let routes = [...config.routes];

	routes = routes.map(route => {
		let auth = config.auth || config.auth === null ? config.auth : defaultAuth;
		auth = route.auth || route.auth === null ? route.auth : auth;
		const settings = merge({}, config.settings, route.settings);

		return { ...route, settings, auth };
	});

	return [...routes];
}

function generateRoutesFromConfigs(configs: AppRouteGroup[], defaultAuth?: AppRouteAuthFunction) {
	let allRoutes: AppRoute[] = [];
	configs.forEach(config => {
		allRoutes = [...allRoutes, ...setRoutes(config, defaultAuth)];
	});
	return allRoutes;
}

export default {
	appendNavItem,
	filterDisabledModules,
	filterRecursive,
	generateRoutesFromConfigs,
	getDerivedRolesFromAuthRole,
	getFlatNavigation,
	getTranslatedRoutes,
	hasPermission,
	prependNavItem,
	removeNavItem,
	updateNavItem
};
